// A CEL expression editor: highlighting, linting from the admin API (the
// same checker and scope the build uses), and autocomplete for the columns
// in scope and the functions builds allow.
import { autocompletion, closeBrackets, completionKeymap, type Completion, type CompletionContext } from '@codemirror/autocomplete'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { bracketMatching } from '@codemirror/language'
import { linter, lintGutter, type Diagnostic } from '@codemirror/lint'
import { EditorState } from '@codemirror/state'
import { EditorView, keymap, placeholder as placeholderExt } from '@codemirror/view'
import { useEffect, useLayoutEffect, useRef } from 'react'

import type { CelFunction, ExprCheck } from '../api'
import { celHighlight, celLanguage } from './language'

/** UTF-16 position of a code-point offset (the API counts code points). */
function utf16(text: string, cp: number): number {
  return Array.from(text).slice(0, cp).join('').length
}

const theme = EditorView.theme({
  '&': { background: 'var(--code)', color: 'var(--codet)', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12.5px' },
  '&.cm-focused': { outline: '2px solid var(--accent)', outlineOffset: '1px' },
  '.cm-content': { fontFamily: 'var(--mono)', padding: '6px 0', caretColor: 'var(--text)' },
  '.cm-line': { padding: '0 8px' },
  '.cm-placeholder': { color: 'var(--muted)' },
  '.cm-gutters': { background: 'transparent', border: 'none' },
  '.cm-tooltip': { background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '6px' },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': { background: 'var(--el)', color: 'var(--text)' },
  '.cm-completionDetail': { color: 'var(--muted)', fontStyle: 'normal', marginLeft: '8px' },
  '.cm-diagnostic-error': { borderLeftColor: 'var(--bad)' },
  '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': { background: 'var(--info-bg) !important' },
})

interface Props {
  value: string
  onChange: (v: string) => void
  /** Type-checks the expression (debounced by the linter). */
  check: (src: string) => Promise<ExprCheck>
  functions: CelFunction[]
  onChecked?: (c: ExprCheck | null) => void
  readOnly?: boolean
  placeholder?: string
  ariaLabel?: string
}

export function CelEditor({ value, onChange, check, functions, onChecked, readOnly, placeholder, ariaLabel }: Props) {
  const host = useRef<HTMLDivElement>(null)
  const view = useRef<EditorView | null>(null)
  // Latest props for the extensions (created once).
  const latest = useRef({ onChange, check, functions, onChecked, variables: [] as { name: string; type: string }[] })
  useLayoutEffect(() => {
    latest.current = { ...latest.current, onChange, check, functions, onChecked }
  })

  useEffect(() => {
    const complete = (ctx: CompletionContext) => {
      const word = ctx.matchBefore(/[A-Za-z_][\w.]*\.?|\./)
      if (!word || (word.from === word.to && !ctx.explicit)) return null
      const text = word.text
      const dot = text.lastIndexOf('.')
      const { variables, functions } = latest.current
      if (dot >= 0) {
        const base = text.slice(0, dot)
        const cols = variables.filter((v) => v.name.startsWith(base + '.'))
        // alias.column, math.round, regex.extract, ...
        const qualified: Completion[] = cols.map((v) => ({ label: v.name.slice(base.length + 1), type: 'variable', detail: v.type }))
        for (const f of functions) {
          if (!f.member && f.name.startsWith(base + '.')) qualified.push({ label: f.name.slice(base.length + 1), type: 'function', detail: f.signature, info: f.doc })
        }
        if (qualified.length > 0) return { from: word.from + dot + 1, options: qualified }
        return {
          from: word.from + dot + 1,
          options: functions.filter((f) => f.member).map((f) => ({ label: f.name, type: 'method', detail: f.signature, info: f.doc, apply: f.name + '(' })),
        }
      }
      const opts: Completion[] = []
      const aliases = new Set<string>()
      for (const v of variables) {
        if (v.name.includes('.')) aliases.add(v.name.split('.')[0])
        else opts.push({ label: v.name, type: 'variable', detail: v.type })
      }
      for (const a of aliases) opts.push({ label: a, type: 'namespace', detail: 'input' })
      for (const f of functions) {
        if (!f.member && !f.name.includes('.')) opts.push({ label: f.name, type: 'function', detail: f.signature, info: f.doc, apply: f.name + '(' })
      }
      for (const ns of ['math', 'regex', 'sets', 'lists']) opts.push({ label: ns, type: 'namespace' })
      for (const k of ['true', 'false', 'null', 'in']) opts.push({ label: k, type: 'keyword' })
      return { from: word.from, options: opts }
    }
    const lint = linter(
      async (v) => {
        const src = v.state.doc.toString()
        if (src.trim() === '') {
          latest.current.onChecked?.(null)
          return []
        }
        try {
          const res = await latest.current.check(src)
          latest.current.variables = res.variables
          latest.current.onChecked?.(res)
          return res.diagnostics.map<Diagnostic>((d) => {
            const from = Math.min(utf16(src, d.start), src.length)
            return { from, to: Math.max(from, Math.min(utf16(src, d.end), src.length)), severity: 'error', message: d.message }
          })
        } catch {
          return []
        }
      },
      { delay: 350 },
    )
    const v = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: value,
        extensions: [
          history(),
          closeBrackets(),
          bracketMatching(),
          keymap.of([...completionKeymap, ...defaultKeymap, ...historyKeymap]),
          celLanguage,
          celHighlight,
          lint,
          lintGutter(),
          autocompletion({ override: [complete], activateOnTyping: true }),
          EditorView.lineWrapping,
          EditorState.readOnly.of(!!readOnly),
          EditorView.contentAttributes.of({ 'aria-label': ariaLabel ?? 'CEL expression' }),
          placeholderExt(placeholder ?? ''),
          theme,
          EditorView.updateListener.of((u) => {
            if (u.docChanged) latest.current.onChange(u.state.doc.toString())
          }),
        ],
      }),
    })
    view.current = v
    return () => v.destroy()
    // The editor is created once; value changes from outside are synced below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly])

  useEffect(() => {
    const v = view.current
    if (v && v.state.doc.toString() !== value) {
      v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: value } })
    }
  }, [value])

  return <div ref={host} className="cel-editor" />
}
